// ── RA.3b Risk Acceptance hooks ───────────────────────────────────────────────
//
// All RA.3b hooks consolidated in one file per cluster single-file hook pattern
// (cf. useVendors.ts). React Query queryKeys include tenantId for cache isolation.
//
// Endpoint inventory (§6.2):
//   GET  /risk-acceptances/:id      — useRiskAcceptance
//   GET  /risk-acceptances          — useRiskAcceptances / useDashboardVendorRisks
//   POST /risk-acceptances          — useCreateRiskAcceptance
//   PATCH /risk-acceptances/:id     — useUpdateDraftRiskAcceptance
//   POST /risk-acceptances/:id/submit         — useSubmitRiskAcceptance
//   POST /risk-acceptances/:id/resume-draft   — useResumeDraft
//   POST /risk-acceptances/:id/{approve|deny|return} — useRiskAcceptanceAction

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  RaEntry,
  RaListResult,
  RaListFilters,
  CreateRaPayload,
  UpdateRaPayload,
  ApprovePayload,
  DenyPayload,
  ReturnPayload,
  CloseAsMitigatedPayload,
  RevokePayload,
  RequestRenewalPayload,
  RaOverview,
} from '@/api/types/risk-acceptance.types';

// ── useRiskAcceptance — GET /risk-acceptances/:id ─────────────────────────────

export function useRiskAcceptance(id: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['risk-acceptance', tenantId, id],
    queryFn: async (): Promise<RaEntry> => {
      const { data } = await apiClient.get<{ data: RaEntry }>(
        `/risk-acceptances/${id}`,
      );
      if (!data.data) throw new Error('Risk response not found');
      return data.data;
    },
    enabled: isAuthenticated && !!id,
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
}

// ── useRiskAcceptances — GET /risk-acceptances (list with filters) ────────────

export function useRiskAcceptances(filters: RaListFilters = {}) {
  const { tenantId, isAuthenticated } = useAuthStore();

  const params = new URLSearchParams();
  if (filters.workflow_status) params.set('workflow_status', filters.workflow_status);
  if (filters.closure_reason) params.set('closure_reason', filters.closure_reason);
  if (filters.vendor_id) params.set('vendor_id', filters.vendor_id);
  if (filters.requires_action_by_user != null) {
    params.set('requires_action_by_user', String(filters.requires_action_by_user));
  }
  // RA.5: smart filter — approaching expiry checkbox
  if (filters.approaching_expiry != null) {
    params.set('approaching_expiry', String(filters.approaching_expiry));
  }
  if (filters.has_renewals != null) {
    params.set('has_renewals', String(filters.has_renewals));
  }
  if (filters.risk_category) params.set('risk_category', filters.risk_category);
  if (filters.search) params.set('search', filters.search);
  params.set('limit', String(filters.limit ?? 25));
  params.set('offset', String(filters.offset ?? 0));
  params.set('sort_by', filters.sort_by ?? 'updated_at');
  params.set('sort_order', filters.sort_order ?? 'desc');

  return useQuery({
    queryKey: ['risk-acceptances', tenantId, filters],
    queryFn: async (): Promise<RaListResult> => {
      const { data } = await apiClient.get<{ data: RaListResult }>(
        `/risk-acceptances?${params.toString()}`,
      );
      return data.data ?? { items: [], total: 0, limit: filters.limit ?? 25, offset: filters.offset ?? 0 };
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// ── useRaOverview — GET /risk-acceptances/overview (operations-workspace aggregate) ──

export function useRaOverview() {
  const { tenantId, isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['risk-acceptances-overview', tenantId],
    queryFn: async (): Promise<RaOverview> => {
      const { data } = await apiClient.get<{ data: RaOverview }>(`/risk-acceptances/overview`);
      return data.data;
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── useDashboardVendorRisks — GET /risk-acceptances?requires_action_by_user=true&limit=5 ──
//
// Powers "Vendor Risks Requiring Attention" dashboard tile (§3.9 / D-RA.3b-PE-8).
// Fetches up to 5 entries requiring current user's action (approver + submitter buckets).

export function useDashboardVendorRisks() {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['dashboard-vendor-risks', tenantId],
    queryFn: async (): Promise<RaEntry[]> => {
      const { data } = await apiClient.get<{ data: RaListResult }>(
        `/risk-acceptances?requires_action_by_user=true&limit=5&sort_by=updated_at&sort_order=desc`,
      );
      return data.data?.items ?? [];
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── useCreateRiskAcceptance — POST /risk-acceptances ─────────────────────────
//
// Supports both create mode (mode='draft' or mode='submit') per §3.4.

export function useCreateRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: CreateRaPayload): Promise<{ data: RaEntry; fieldErrors?: Record<string, string> }> => {
      const res = await apiClient.post<{ data: RaEntry; errors: unknown }>(
        '/risk-acceptances',
        dto,
      );
      return { data: res.data.data };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-vendor-risks', tenantId] });
    },
  });
}

// ── useUpdateDraftRiskAcceptance — PATCH /risk-acceptances/:id ────────────────

export function useUpdateDraftRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateRaPayload }): Promise<RaEntry> => {
      const { data } = await apiClient.patch<{ data: RaEntry }>(
        `/risk-acceptances/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
    },
  });
}

// ── useSubmitRiskAcceptance — POST /risk-acceptances/:id/submit ───────────────

export function useSubmitRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string): Promise<RaEntry> => {
      const { data } = await apiClient.post<{ data: RaEntry }>(
        `/risk-acceptances/${id}/submit`,
        {},
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-vendor-risks', tenantId] });
    },
  });
}

// ── useResumeDraft — POST /risk-acceptances/:id/resume-draft ──────────────────
//
// RETURNED → DRAFT. Used from detail page "Resume Draft" button (§3.5 / D-RA.3b-ARCH-6).
// On success: caller routes to /risk-acceptances/new?from=:id for pre-populated edit.

export function useResumeDraft() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string): Promise<RaEntry> => {
      const { data } = await apiClient.post<{ data: RaEntry }>(
        `/risk-acceptances/${id}/resume-draft`,
        {},
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
    },
  });
}

// ── useRiskAcceptanceAction — POST /risk-acceptances/:id/{action} ─────────────
//
// Generic mutation for Approver actions. Used by <RiskAcceptanceActionModal> (§3.7).
// RA.4: 5 action types. RA.5: extended to 7 (approve-renewal + deny-renewal post to
// EXISTING /approve + /deny endpoints on child entries — no new endpoints).
// approve-renewal → POST /:id/approve (child entry id)
// deny-renewal    → POST /:id/deny   (child entry id)

export type RaActionType =
  | 'approve'
  | 'deny'
  | 'return'
  | 'close-as-mitigated'
  | 'revoke'
  | 'approve-renewal'
  | 'deny-renewal';

export type RaActionPayload = ApprovePayload | DenyPayload | ReturnPayload | CloseAsMitigatedPayload | RevokePayload;

// RA.5: maps RaActionType to the actual HTTP path segment
function actionToEndpoint(action: RaActionType): string {
  if (action === 'approve-renewal') return 'approve';
  if (action === 'deny-renewal')    return 'deny';
  return action;
}

export function useRiskAcceptanceAction(action: RaActionType) {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: RaActionPayload }): Promise<RaEntry> => {
      const { data } = await apiClient.post<{ data: RaEntry }>(
        `/risk-acceptances/${id}/${actionToEndpoint(action)}`,
        payload,
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-vendor-risks', tenantId] });
      // Invalidate vendor-scoped list for VendorDetailPage tab
      qc.invalidateQueries({ queryKey: ['vendor-risk-acceptances', tenantId] });
      toast.success(
        action === 'approve'         ? 'Risk response approved' :
        action === 'approve-renewal' ? 'Renewal approved' :
        action === 'deny'            ? 'Risk response denied' :
        action === 'deny-renewal'    ? 'Renewal denied' :
        action === 'close-as-mitigated' ? 'Risk response closed as mitigated' :
        action === 'revoke'          ? 'Approval revoked' :
        'Returned for revision',
      );
    },
  });
}

// ── useRequestRenewal — POST /risk-acceptances/:id/request-renewal ────────────
//
// RA.5: creates a child RA entry (renewal request) with parent_acceptance_id server-set.
// On success: navigates to child entry detail page (caller's onSuccess handles navigate).

export function useRequestRenewal() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      parentId,
      payload,
    }: {
      parentId: string;
      payload: RequestRenewalPayload;
    }): Promise<RaEntry> => {
      const { data } = await apiClient.post<{ data: RaEntry }>(
        `/risk-acceptances/${parentId}/request-renewal`,
        payload,
      );
      return data.data;
    },
    onSuccess: (child) => {
      // Invalidate parent entry (renewal_window_open may change) + list + dashboard
      qc.invalidateQueries({ queryKey: ['risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-vendor-risks', tenantId] });
      qc.invalidateQueries({ queryKey: ['vendor-risk-acceptances', tenantId] });
      // Also invalidate parent entry cache so renewal_window_open refreshes
      if (child.parent_acceptance_id) {
        qc.invalidateQueries({ queryKey: ['risk-acceptance', tenantId, child.parent_acceptance_id] });
      }
      toast.success('Renewal request submitted for approval');
    },
  });
}
